import React, { Component, ComponentType } from 'react';

/**
 * SetDifference (same as Exclude)
 * @desc Set difference of given union types `A` and `B`
 */
export type SetDifference<A, B> = A extends B ? never : A;

/**
 * SetComplement
 * @desc Set complement of given union types `A` and (it's subset) `A1`
 */
export type SetComplement<A, A1 extends A> = SetDifference<A, A1>;

/**
 * Subtract
 * @desc From `T` remove properties that exist in `T1` (`T1` is a subtype of `T`)
 */
export type Subtract<T extends T1, T1 extends object> = Pick<T, SetComplement<keyof T, keyof T1>>;

export interface WrappedScreenClassProps {
  screenClass: number;
}

interface WithScreenClassState {
  screenClass: number;
}

// Breakpoints must be in order from smallest to largest
export default function withScreenClass(breakpoints: number[]) {
  return <AllProps extends WrappedScreenClassProps>(WrappedComponent: ComponentType<AllProps>) => {
    type InjectedProps = WrappedScreenClassProps;
    // WrappedComponent accepts AllProps (injected + nonInjected)
    // but the WithScreenClass component that we export only accepts nonInjectedProps (then injects the additional props)
    // we `calculate` the nonInjectedProps by subtracting the InjectedProps from AllProps
    type NonInjectedProps = Subtract<AllProps, InjectedProps>;
    class WithScreenClass extends Component<NonInjectedProps, WithScreenClassState> {
      displayName = `WithScreenClass(${WrappedComponent.displayName ||
        WrappedComponent.name ||
        'Component'})`;

      listeners: ((e: MediaQueryListEvent) => void)[] = [];
      mediaQueryLists: MediaQueryList[] = [];

      constructor(props: NonInjectedProps) {
        super(props);

        this.mediaQueryLists = breakpoints.map(breakpoint =>
          window.matchMedia(`(max-width: ${breakpoint}px)`),
        );

        // Determine the initial screen class by finding the first matching media query
        const matchingQueryIndex = this.mediaQueryLists.findIndex(
          mediaQueryList => mediaQueryList.matches,
        );

        this.state = {
          screenClass: matchingQueryIndex >= 0 ? matchingQueryIndex : breakpoints.length,
        };
      }

      componentDidMount() {
        this.listeners = this.mediaQueryLists.map((mediaQueryList, index) => {
          const listener = (e: MediaQueryListEvent) => {
            const { screenClass } = this.state;
            const newScreenClass = e.matches ? index : index + 1;

            // Only change the screen class in the direction the width is changing
            // (a match means the browser is getting narrower)
            if (e.matches ? newScreenClass < screenClass : newScreenClass > screenClass) {
              this.setState({ screenClass: newScreenClass });
            }
          };

          mediaQueryList.addListener(listener);

          return listener;
        });
      }

      componentWillUnmount() {
        this.mediaQueryLists.forEach((mediaQueryList, index) =>
          mediaQueryList.removeListener(this.listeners[index]),
        );
      }

      render() {
        const { screenClass } = this.state;

        const injectedProps: InjectedProps = {
          screenClass,
        };

        const wrappedComponentProps: AllProps = {
          ...this.props,
          ...injectedProps,
        } as AllProps; // hack: cast to AllProps - hints to TypeScript that (AllProps - InjectedProps) + InjectedProps = AllProps -_-

        return <WrappedComponent {...wrappedComponentProps} />;
      }
    }

    return WithScreenClass;
  };
}
